import { EventBus } from '../../core/EventBus.js';
import { Modal }    from '../components/Modal.js';
import { getToken } from '../../core/ServerConnection.js';

const CATEGORIES = ['gameplay', 'ui', 'economy', 'ships', 'contracts', 'map', 'performance', 'other'];

export class FeedbackModal {
  constructor() {
    this.modal = new Modal('feedback-modal', { title: 'Tester Feedback' });
    this.data = null;
    this.activeTab = 'recent';

    EventBus.on('ui:feedbackOpen', () => this.open());
  }

  async open() {
    await this._fetchData();
    this._render();
    this._bindEvents();
    this.modal.open();
  }

  async _fetchData() {
    try {
      const res = await fetch('/api/feedback', { headers: this._authHeaders() });
      if (res.ok) this.data = await res.json();
    } catch (e) {
      console.error('Failed to fetch feedback:', e);
    }
    if (!this.data) this.data = { inbox: [], grouped: [], status: { implemented: [], accepted: [], rejected: [], deferred: [] }, conflicts: [] };
  }

  _render() {
    const d = this.data;
    if (!d.isAdmin) this.activeTab = 'recent';
    const counts = {
      recent: d.inbox.length,
      grouped: d.grouped.length,
      approved: d.status.accepted.length,
      rejected: d.status.rejected.length,
      conflicts: this._openConflicts().length,
      done: d.status.implemented.length
    };

    const catOptions = CATEGORIES.map(c => `<option value="${c}">${c}</option>`).join('');

    this.modal.setBody(`
      <div class="fb-form">
        <h4>Submit Feedback</h4>
        <div class="fb-form-row">
          <select id="fb-category" class="fb-input">${catOptions}</select>
        </div>
        <textarea id="fb-text" rows="3" placeholder="Describe the bug, suggestion, or idea..." class="fb-input fb-textarea"></textarea>
        <button class="btn btn-primary" id="fb-submit">Submit Feedback</button>
      </div>

      <hr class="fb-divider">

      <div class="fb-tabs" id="fb-tabs">
        ${this._tabBtn('recent', d.isAdmin ? 'Recent' : 'My Feedback', counts.recent)}
        ${d.isAdmin ? `
          ${this._tabBtn('grouped', 'Grouped', counts.grouped)}
          ${this._tabBtn('approved', 'Approved', counts.approved)}
          ${this._tabBtn('rejected', 'Rejected', counts.rejected)}
          ${this._tabBtn('conflicts', 'Conflicts', counts.conflicts)}
          ${this._tabBtn('done', 'Done', counts.done)}` : ''}
      </div>

      <div class="fb-list-content" id="fb-list-content">
        ${this._renderTab()}
      </div>
    `);
  }

  _tabBtn(id, label, count) {
    const active = this.activeTab === id ? ' active' : '';
    return `<button class="fb-tab${active}" data-fb-tab="${id}">${label} (${count})</button>`;
  }

  _renderTab() {
    const d = this.data;
    switch (this.activeTab) {
      case 'recent':   return this._renderRecent(d.inbox);
      case 'grouped':  return this._renderGrouped(d.grouped);
      case 'approved': return this._renderStatusList(d.status.accepted, 'accepted');
      case 'rejected': return this._renderStatusList(d.status.rejected, 'rejected');
      case 'conflicts':return this._renderConflicts(this._openConflicts());
      case 'done':     return this._renderStatusList(d.status.implemented, 'implemented');
      default:         return '';
    }
  }

  _renderRecent(entries) {
    if (!entries.length) return '<p class="panel-empty">No feedback yet.</p>';
    const sorted = [...entries].sort((a, b) => (b.number || 0) - (a.number || 0));
    return sorted.map(e => `
      <div class="fb-entry">
        <div class="fb-entry-header">
          <span class="fb-number">#${e.number}</span>
          <span class="fb-badge fb-badge-cat">${e.category || '—'}</span>
          <span class="fb-badge fb-badge-status fb-status-${(e.status || 'new').replace(/\s+/g, '-')}">${e.status || 'new'}</span>
          <span class="fb-date">${e.date || ''}</span>
        </div>
        <div class="fb-entry-text">${this._esc(e.description || e.original || '')}</div>
        ${e.implementationNote ? `<div class="fb-entry-note">${this._esc(e.implementationNote)}</div>` : ''}
        <div class="fb-entry-author">— ${this._esc(e.from || 'Anonymous')}</div>
      </div>
    `).join('');
  }

  _renderGrouped(themes) {
    if (!themes.length) return '<p class="panel-empty">No grouped themes yet.</p>';
    return themes.map(t => `
      <div class="fb-entry">
        <div class="fb-entry-header">
          <strong>${this._esc(t.title)}</strong>
          ${t.suggestions?.length ? `<span class="fb-refs">${t.suggestions.map(s => '#' + s).join(', ')}</span>` : ''}
        </div>
        ${t.summary ? `<div class="fb-entry-text">${this._esc(t.summary)}</div>` : ''}
        <div class="fb-entry-meta">
          ${t.impact ? `<span>Impact: ${this._esc(t.impact)}</span>` : ''}
          ${t.effort ? `<span>Effort: ${this._esc(t.effort)}</span>` : ''}
        </div>
      </div>
    `).join('');
  }

  _renderStatusList(items, type) {
    if (!items.length) return `<p class="panel-empty">No ${type} feedback.</p>`;
    return items.map(e => `
      <div class="fb-entry fb-entry-${type}">
        <div class="fb-entry-text">${this._esc(e.text)}</div>
        ${e.suggestions?.length ? `<div class="fb-refs">${e.suggestions.map(s => '#' + s).join(', ')}</div>` : ''}
      </div>
    `).join('');
  }

  /** Unresolved conflicts only — resolved ones are history in conflicts.md. */
  _openConflicts() {
    return this.data.conflicts.filter(c => !c.resolved);
  }

  _renderConflicts(conflicts) {
    if (!conflicts.length) return '<p class="panel-empty">No conflicts detected.</p>';
    return conflicts.map(c => `
      <div class="fb-entry fb-entry-conflict">
        <div class="fb-entry-header"><strong>${this._esc(c.title)}</strong></div>
        <div class="fb-entry-text">${this._esc(c.details)}</div>
        ${c.suggestions?.length ? `<div class="fb-refs">${c.suggestions.map(s => '#' + s).join(', ')}</div>` : ''}
      </div>
    `).join('');
  }

  _bindEvents() {
    // Tab switching
    this.modal.el.querySelectorAll('[data-fb-tab]').forEach(btn => {
      btn.addEventListener('click', () => {
        this.activeTab = btn.dataset.fbTab;
        // Update tab active state
        this.modal.el.querySelectorAll('.fb-tab').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');
        document.getElementById('fb-list-content').innerHTML = this._renderTab();
      });
    });

    // Submit
    const submitBtn = document.getElementById('fb-submit');
    if (submitBtn) {
      submitBtn.addEventListener('click', () => this._submit());
    }
  }

  async _submit() {
    const category = document.getElementById('fb-category')?.value;
    const text = document.getElementById('fb-text')?.value.trim();

    if (!text) {
      EventBus.emit('ui:notification', { message: 'Please write your feedback first.', type: 'warning' });
      return;
    }

    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...this._authHeaders() },
        body: JSON.stringify({ category, text })
      });
      const result = await res.json();
      if (result.success) {
        EventBus.emit('ui:notification', { message: `Feedback #${result.number} submitted!`, type: 'success' });
        document.getElementById('fb-text').value = '';
        // Refresh list
        await this._fetchData();
        this.activeTab = 'recent';
        this._render();
        this._bindEvents();
      } else {
        EventBus.emit('ui:notification', { message: result.error || 'Failed to submit.', type: 'error' });
      }
    } catch (e) {
      EventBus.emit('ui:notification', { message: 'Network error submitting feedback.', type: 'error' });
    }
  }

  _authHeaders() {
    const token = getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }

  _esc(str) {
    if (!str) return '';
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }
}
